import keyBy from 'lodash-es/keyBy';
import omit from 'lodash-es/omit';
import { uniqObjectMerge } from '../../utils/arrayUtils';
import { NewDealerActionKeys as keys } from './newDealerActionKeys';
import { NewDealerActionTypes } from './newDealerActionTypes';
import {
  INewDealerRequest,
  INewDealerRequestCart,
  INewDealerRequestCompleteEvent,
  INewDealerRequestEvent,
  INewDealerRequestMin
} from './newDealerInterfaces';
import { convertNewDealerRequestEventRequestToString } from './newDealerSelectors';

export interface INewDealerState {
  businessEntityRequestMap: { [index: number]: number };
  carts: { [index: number]: { [index: string]: INewDealerRequestCart } };
  completeEvents: INewDealerRequestCompleteEvent[];
  events: INewDealerRequestEvent[];
  requestMins: INewDealerRequestMin[];
  requests: { [index: number]: INewDealerRequest };
  savingEvents: string[];
  searchResultHash: { [index: number]: { [index: string]: string[] } };
  searchingCatalog: boolean;
}

const initState: INewDealerState = {
  businessEntityRequestMap: {},
  carts: {},
  completeEvents: [],
  events: [],
  requestMins: [],
  requests: {},
  savingEvents: [],
  searchResultHash: {},
  // tslint:disable-next-line:object-literal-sort-keys
  searchingCatalog: false
};

export function newDealerReducer(state: INewDealerState = initState, action: NewDealerActionTypes): INewDealerState {
  switch (action.type) {
    case keys.REMOVE_NEW_DEALER_CART:
      return {
        ...state,
        carts: {
          ...state.carts,
          [action.payload.newDealerRequestId]: omit(
            state.carts[action.payload.newDealerRequestId],
            action.payload.brand
          )
        }
      };
    case keys.REMOVE_NEW_DEALER_COMPLETE_REQUEST_EVENT:
      return {
        ...state,
        completeEvents: state.completeEvents.filter(
          x =>
            !(
              x.newDealerRequestId === action.payload.newDealerRequestId &&
              x.newDealerRequestCompleteEventTypeId === action.payload.newDealerRequestCompleteEventTypeId
            )
        )
      };
    case keys.REMOVE_NEW_DEALER_REQUEST_EVENT:
      return {
        ...state,
        events: state.events.filter(x => x.newDealerRequestEventId !== action.payload.newDealerRequestEventId)
      };
    case keys.SET_NEW_DEALER_REQUEST:
      return {
        ...state,
        businessEntityRequestMap: {
          ...state.businessEntityRequestMap,
          [action.payload.businessEntityId]: action.payload.newDealerRequestId
        },
        carts: {
          ...state.carts,
          [action.payload.newDealerRequestId]: keyBy(action.payload.carts, x => x.brand)
        },
        completeEvents: uniqObjectMerge(
          state.completeEvents,
          action.payload.completeEvents,
          x => `${x.newDealerRequestId}_${x.newDealerRequestCompleteEventTypeId}`
        ),
        events: uniqObjectMerge(state.events, action.payload.events, x => x.newDealerRequestEventId),
        requests: {
          ...state.requests,
          [action.payload.newDealerRequestId]: omit(action.payload, 'events')
        }
      };
    case keys.SET_NEW_DEALER_REQUEST_MINS:
      return {
        ...state,
        requestMins: uniqObjectMerge(state.requestMins, action.payload, x => x.newDealerRequestId)
      };
    case keys.SET_NEW_DEALER_REQUEST_COMPLETE_EVENT:
      return {
        ...state,
        completeEvents: uniqObjectMerge(
          state.completeEvents,
          [action.payload],
          x => `${x.newDealerRequestId}_${x.newDealerRequestCompleteEventTypeId}`
        )
      };
    case keys.SET_NEW_DEALER_REQUEST_EVENT:
      return {
        ...state,
        events: uniqObjectMerge(state.events, [action.payload], x => x.newDealerRequestEventId)
      };
    case keys.SET_NEW_DEALER_PROGRAM:
      return {
        ...state,
        carts: {
          ...state.carts,
          [action.payload.newDealerRequestId]: {
            ...state.carts[action.payload.newDealerRequestId],
            [action.payload.brand]: action.payload
          }
        }
      };
    case keys.SET_NEW_DEALER_SEARCH:
      return {
        ...state,
        searchResultHash: {
          ...state.searchResultHash,
          [action.payload.cartId]: {
            ...state.searchResultHash[action.payload.cartId],
            [action.payload.searchTerm]: action.payload.products.map(x => x.itemId)
          }
        }
      };
    case keys.TOGGLE_NEW_DEALER_CATALOG_SEARCH:
      return {
        ...state,
        searchingCatalog: !state.searchingCatalog
      };
    case keys.TOGGLE_NEW_DEALER_EVENT_SAVE:
      const key = convertNewDealerRequestEventRequestToString(action.payload);
      return {
        ...state,
        savingEvents: state.savingEvents.includes(key)
          ? state.savingEvents.filter(x => x !== key)
          : [...state.savingEvents, key]
      };

    default:
      return state;
  }
}
